import React, { useState, useEffect } from 'react';

const API = 'https://groovie-business-api-production.up.railway.app/api/v1';

const Svg = ({ type, size = 16 }) => {
  const icons = {
    check: <><polyline points="20,6 9,17 4,12"/></>,
    star: <><polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/></>,
    zap: <><polygon points="13,2 3,14 12,14 11,22 21,10 12,10"/></>,
    crown: <><path d="M2 4l3 12h14l3-12-5 4-5-4-5 4z"/><path d="M5 16h14v2H5z"/></>,
    card: <><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></>,
    download: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></>,
    external: <><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15,3 21,3 21,9"/><line x1="10" y1="14" x2="21" y2="3"/></>,
    alert: <><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>,
    shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></>,
    x: <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
  };
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icons[type]}</svg>;
};

// S1: single locked plan \u2014 $89/location/month (matches the marketing site + the frontdoor).
const BUSINESS_PLAN = {
  tier: 'business', name: 'Groovie for Business', price: 89, icon: 'zap',
  accent: '#7c3aed', bg: '#faf5ff', border: '#7c3aed', badgeBg: '#f5f3ff', badgeText: '#7c3aed',
  subtitle: 'Your own branded radio station \u2014 unlimited zones per location',
  features: [
    { text: 'Your own branded radio station \u2014 unlimited channels per location', included: true, highlight: true },
    { text: 'Rights-controlled, Groovie-produced catalog', included: true, highlight: true },
    { text: 'AI announcements \u2014 type it, it airs', included: true },
    { text: 'Custom jingles & DJ shout-outs', included: true },
    { text: 'Calendar automation & scheduling', included: true },
    { text: 'Multi-location management \u2014 centralized control, per-location channels, and announcements to one location or all', included: true },
    { text: 'Analytics & branded QR codes', included: true },
    { text: 'Compliance dashboard', included: true },
    { text: 'Cancel anytime \u2014 effective at the end of your paid month', included: true },
  ],
};
const plans = [BUSINESS_PLAN];

export default function Billing() {
  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [toast, setToast] = useState(null);
  const [annual] = useState(false);  // monthly-only per S1 spec (no annual toggle)
  const [authOpen, setAuthOpen] = useState(false);      // S1: affirmative-authorization screen
  const [authChecked, setAuthChecked] = useState(false); // the separate affirmative action

  useEffect(() => {
    fetchBilling();
    const params = new URLSearchParams(window.location.search);
    if (params.get('success')) {
      setToast({ type: 'success', message: 'Successfully subscribed to ' + (params.get('tier') || '') + ' plan!' });
      window.history.replaceState({}, '', '/billing');
    }
    if (params.get('canceled')) {
      setToast({ type: 'info', message: 'Checkout canceled. No changes made.' });
      window.history.replaceState({}, '', '/billing');
    }
  }, []);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const fetchBilling = async () => {
    try {
      const res = await fetch(API + '/billing/current', { headers });
      if (res.ok) {
        const data = await res.json();
        setBilling(data);
      }
    } catch (err) {
      console.error('Billing fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // S1: checkout only ever runs with an explicit affirmative authorization (authorized=true),
  // captured on the disclosure screen below. The backend also refuses a charge without it.
  const handleUpgrade = async (tier, authorized = false) => {
    setUpgrading(tier);
    try {
      const res = await fetch(API + '/billing/checkout', {
        method: 'POST', headers,
        body: JSON.stringify({ tier: tier, interval: annual ? 'year' : 'month', authorized: authorized }),
      });
      const data = await res.json();
      if (res.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setToast({ type: 'error', message: data.detail || 'Failed to start checkout' });
      }
    } catch (err) {
      setToast({ type: 'error', message: 'Checkout error. Please try again.' });
    } finally {
      setUpgrading(null);
    }
  };

  // S1: open the authorization screen; only its checked, explicit action proceeds to checkout.
  const openAuthorization = () => { setAuthChecked(false); setAuthOpen(true); };
  const confirmAuthorizedSubscribe = () => {
    if (!authChecked) return;
    setAuthOpen(false);
    handleUpgrade('business', true);
  };

  const handleManageBilling = async () => {
    try {
      const res = await fetch(API + '/billing/portal', { method: 'POST', headers });
      const data = await res.json();
      if (res.ok && data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        setToast({ type: 'error', message: data.detail || 'Unable to open billing portal' });
      }
    } catch (err) {
      setToast({ type: 'error', message: 'Portal error. Please try again.' });
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '\u2014';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid #e9d5ff', borderTopColor: '#7c3aed', animation: 'spin 0.8s linear infinite' }} />
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      </div>
    );
  }

  const currentTier = billing ? billing.tier : 'business';
  const currentPlanIdx = plans.findIndex(function(p) { return p.tier === currentTier; });
  const currentPlanDisplay = plans.find(function(p) { return p.tier === currentTier; });

  // Wave 2 per-location quantity billing: the amount Stripe actually bills = $89 x quantity.
  const bill = (billing && billing.billing) ? billing.billing : null;
  const activeLocations = (billing && billing.usage && typeof billing.usage.locations === 'number')
    ? billing.usage.locations : (bill ? (bill.active_locations || 0) : 0);
  const billedQty = bill && bill.billed_quantity ? bill.billed_quantity : 0;
  const isSubscribed = billing && (billing.status === 'active' || billing.status === 'past_due');
  const monthlyTotal = isSubscribed && billedQty ? billedQty * 89 : null;
  // If subscribed, we bill the quantity; otherwise (trial) we'd bill the current location count at checkout.
  const checkoutQty = Math.max(1, activeLocations || 0);

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Outfit', 'DM Sans', sans-serif" }}>

      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 100, padding: '14px 24px', borderRadius: 12,
          fontSize: 14, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          background: toast.type === 'success' ? '#ecfdf5' : toast.type === 'error' ? '#fef2f2' : '#eff6ff',
          color: toast.type === 'success' ? '#065f46' : toast.type === 'error' ? '#991b1b' : '#1e40af',
          border: '1px solid ' + (toast.type === 'success' ? '#a7f3d0' : toast.type === 'error' ? '#fecaca' : '#bfdbfe'),
        }}>{toast.message}</div>
      )}

      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.5px' }}>Billing & Subscription</h1>
        <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>Manage your plan, usage, and payment details</p>
      </div>

      {billing && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
          <div style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            borderRadius: 16, padding: 28, color: '#fff',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <p style={{ fontSize: 13, opacity: 0.8, margin: 0 }}>Current Plan</p>
                <h2 style={{ fontSize: 32, fontWeight: 800, margin: '4px 0 0', letterSpacing: '-1px' }}>
                  {currentPlanDisplay ? currentPlanDisplay.name : 'Groovie for Business'}
                </h2>
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.2)', borderRadius: 12, padding: '8px 16px',
                fontSize: 22, fontWeight: 800, textAlign: 'right',
              }}>
                ${monthlyTotal != null ? monthlyTotal : (currentPlanDisplay ? currentPlanDisplay.price : 89)}<span style={{ fontSize: 13, fontWeight: 500 }}>/mo</span>
                {monthlyTotal != null && (
                  <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.85, marginTop: 2 }}>
                    $89 &times; {billedQty} location{billedQty === 1 ? '' : 's'}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                {billing.status === 'active' ? '\u2705 Active' : billing.status === 'trial' ? '\u23F3 Trial' : billing.status === 'past_due' ? '\u26A0\uFE0F Past Due' : billing.status === 'supported' ? '\uD83C\uDFB5 Supported Mode' : billing.status === 'deactivated' ? '\u23F8\uFE0F Deactivated' : billing.status}
              </span>
              <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                Member since {formatDate(billing.member_since)}
              </span>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
            }}>
              <Svg type="shield" size={16} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>Groovie's rights-controlled, Groovie-produced catalog, cleared for distribution through Groovie</span>
            </div>
            {billing.has_stripe && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button onClick={handleManageBilling} style={{
                  background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)',
                  color: '#fff', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Outfit',sans-serif",
                }}>
                  <Svg type="external" size={14} /> Manage Payment Method
                </button>
                <button onClick={handleManageBilling} title="Cancel your subscription — effective at the end of your current paid month" style={{
                  background: '#ffffff', border: '1px solid #ffffff',
                  color: '#7c3aed', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Outfit',sans-serif",
                }}>
                  <Svg type="x" size={14} /> Cancel subscription
                </button>
              </div>
            )}
          </div>

          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', padding: 28 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 20px' }}>Current Usage</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {[
                { label: 'Locations', used: billing.usage ? billing.usage.locations : 0, max: -1 },
                { label: 'Channels', used: billing.usage ? billing.usage.channels : 0, max: -1 },
                { label: 'Licensed Devices', used: billing.usage ? billing.usage.devices : 0, max: -1 },
              ].map(function(item) {
                var pct = item.max > 0 ? Math.min((item.used / item.max) * 100, 100) : 0;
                var isNearLimit = item.max > 0 && pct >= 80;
                return (
                  <div key={item.label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{item.label}</span>
                      <span style={{ fontSize: 13, color: isNearLimit ? '#dc2626' : '#6b7280', fontWeight: isNearLimit ? 700 : 500 }}>
                        {item.used}{item.max > 0 ? ' / ' + item.max : ''}
                      </span>
                    </div>
                    {item.max > 0 && (
                      <div style={{ height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', borderRadius: 3, width: pct + '%',
                          background: isNearLimit ? '#ef4444' : 'linear-gradient(90deg, #7c3aed, #a855f7)',
                          transition: 'width 0.3s ease',
                        }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {billing.status === 'past_due' && (
              <div style={{
                marginTop: 20, padding: '12px 16px', borderRadius: 10,
                background: '#fef2f2', border: '1px solid #fecaca',
                display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#991b1b',
              }}>
                <Svg type="alert" size={16} />
                <span>Payment overdue. Update your payment method to avoid service interruption.</span>
              </div>
            )}
            {bill && isSubscribed && (bill.drift || bill.sync_pending) && (
              <div style={{
                marginTop: 20, padding: '12px 16px', borderRadius: 10,
                background: '#fffbeb', border: '1px solid #fde68a',
                display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#92400e',
              }}>
                <Svg type="alert" size={16} />
                <span>You're running {activeLocations} location{activeLocations === 1 ? '' : 's'} but billing for {billedQty}. We're syncing this to your next bill; no action needed.</span>
              </div>
            )}
            {(billing.status === 'supported' || billing.status === 'deactivated') && (
              <div style={{
                marginTop: 20, padding: '14px 16px', borderRadius: 10,
                background: billing.status === 'supported' ? '#fffbeb' : '#fef2f2',
                border: '1px solid ' + (billing.status === 'supported' ? '#fde68a' : '#fecaca'),
                fontSize: 13, color: billing.status === 'supported' ? '#92400e' : '#991b1b',
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>
                  {billing.status === 'supported' ? '🎵 Supported Mode' : '⏸️ Account Deactivated'}
                </div>
                <div style={{ marginBottom: 10, lineHeight: 1.5 }}>
                  {billing.status === 'supported'
                    ? 'Your setup is saved and your station is playing Groovie programming with house promos. Subscribe anytime to restore everything instantly.'
                    : 'Your setup is saved for 12 months. Subscribe to reactivate and restore everything instantly.'}
                </div>
                <button onClick={openAuthorization} style={{
                  background: 'linear-gradient(135deg,#7c3aed,#a855f7)', color: '#fff', border: 'none',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Outfit',sans-serif",
                }}>
                  {billing.status === 'supported' ? 'Subscribe' : 'Reactivate'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{
        background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 14,
        padding: '14px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12,
        fontSize: 13, color: '#166534',
      }}>
        <Svg type="shield" size={18} />
        <span>Your subscription plays Groovie's rights-controlled, Groovie-produced catalog, cleared for distribution through Groovie. Your subscription covers Groovie-supplied programming only — not music you supply or play separately.</span>
      </div>

      <div style={{ marginBottom: 32 }}>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
            {billing && billing.status === 'trial' ? 'Choose your plan' : 'Your plan'}
          </h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', maxWidth: 440, gap: 20 }}>
          {plans.map(function(plan, idx) {
            var isCurrent = plan.tier === currentTier;
            var isDowngrade = idx < currentPlanIdx;
            return (
              <div key={plan.tier} style={{
                background: plan.bg, borderRadius: 16, padding: 28,
                border: '2px solid ' + (isCurrent ? plan.accent : plan.border),
                position: 'relative', transition: 'transform 0.15s, box-shadow 0.15s',
                display: 'flex', flexDirection: 'column',
              }}
              onMouseEnter={function(e) { if (!isCurrent) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}}
              onMouseLeave={function(e) { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                {plan.popular && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    background: plan.accent, color: '#fff', fontSize: 11, fontWeight: 700,
                    padding: '4px 16px', borderRadius: 20, whiteSpace: 'nowrap',
                  }}>MOST POPULAR</div>
                )}
                {isCurrent && (
                  <div style={{
                    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                    background: '#059669', color: '#fff', fontSize: 11, fontWeight: 700,
                    padding: '4px 16px', borderRadius: 20, whiteSpace: 'nowrap',
                  }}>CURRENT PLAN</div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: plan.badgeBg, color: plan.accent,
                  }}>
                    <Svg type={plan.icon} size={18} />
                  </div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#111827', margin: 0 }}>{plan.name}</h3>
                </div>
                <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 16px' }}>{plan.subtitle}</p>

                <div style={{ marginBottom: 20 }}>
                  <span style={{ fontSize: 40, fontWeight: 800, color: '#111827', letterSpacing: '-1px' }}>${plan.price}</span>
                  <span style={{ fontSize: 14, color: '#9ca3af' }}>/mo per location</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24, flex: 1 }}>
                  {plan.features.map(function(f) {
                    return (
                      <div key={f.text} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 13, color: f.included ? '#374151' : '#d1d5db',
                        fontWeight: f.highlight ? 600 : 400,
                      }}>
                        <div style={{ color: f.included ? plan.accent : '#d1d5db', flexShrink: 0 }}>
                          <Svg type={f.included ? 'check' : 'x'} size={14} />
                        </div>
                        {f.text}
                      </div>
                    );
                  })}
                </div>

                {isCurrent && billing && billing.status !== 'trial' ? (
                  <div style={{
                    textAlign: 'center', padding: '12px', borderRadius: 10,
                    background: '#f3f4f6', color: '#6b7280', fontSize: 14, fontWeight: 600,
                  }}>Your Current Plan</div>
                ) : isCurrent && billing && billing.status === 'trial' ? (
                  <button onClick={openAuthorization} disabled={upgrading === plan.tier}
                    style={{
                      width: '100%', padding: '13px', borderRadius: 10, border: 'none',
                      background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                      color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                      fontFamily: "'Outfit',sans-serif",
                      opacity: upgrading === plan.tier ? 0.7 : 1,
                    }}>
                    {upgrading === plan.tier ? 'Processing...' : 'Activate Plan'}
                  </button>
                ) : isDowngrade ? (
                  <button onClick={handleManageBilling} style={{
                    width: '100%', padding: '12px', borderRadius: 10,
                    border: '1.5px solid #e5e7eb', background: '#fff',
                    color: '#6b7280', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    fontFamily: "'Outfit',sans-serif",
                  }}>Downgrade</button>
                ) : (
                  <button onClick={openAuthorization} disabled={upgrading === plan.tier}
                    style={{
                      width: '100%', padding: '13px', borderRadius: 10, border: 'none',
                      background: 'linear-gradient(135deg, ' + plan.accent + ', ' + plan.accent + 'dd)',
                      color: '#fff', fontSize: 14, fontWeight: 700, cursor: upgrading ? 'not-allowed' : 'pointer',
                      fontFamily: "'Outfit',sans-serif",
                      boxShadow: '0 4px 12px ' + plan.accent + '44',
                      opacity: upgrading === plan.tier ? 0.7 : 1,
                    }}>
                    {upgrading === plan.tier ? 'Redirecting...' : 'Subscribe'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {billing && billing.invoices && billing.invoices.length > 0 && (
        <div style={{ marginBottom: 32 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Invoice History</h3>
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Date</th>
                  <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Amount</th>
                  <th style={{ textAlign: 'left', padding: '12px 20px', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Status</th>
                  <th style={{ textAlign: 'right', padding: '12px 20px', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {billing.invoices.map(function(inv) {
                  return (
                    <tr key={inv.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '14px 20px', fontSize: 14, color: '#374151' }}>{formatDate(inv.date)}</td>
                      <td style={{ padding: '14px 20px', fontSize: 14, fontWeight: 600, color: '#111827' }}>${inv.amount.toFixed(2)}</td>
                      <td style={{ padding: '14px 20px' }}>
                        <span style={{
                          padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: inv.status === 'paid' ? '#ecfdf5' : '#fef2f2',
                          color: inv.status === 'paid' ? '#065f46' : '#991b1b',
                        }}>{inv.status === 'paid' ? 'Paid' : inv.status}</span>
                      </td>
                      <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                        {inv.pdf_url && (
                          <a href={inv.pdf_url} target="_blank" rel="noreferrer"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              color: '#7c3aed', fontSize: 13, fontWeight: 600, textDecoration: 'none',
                            }}>
                            <Svg type="download" size={14} /> PDF
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(!billing || !billing.invoices || billing.invoices.length === 0) && (
        <div style={{
          background: '#f9fafb', borderRadius: 14, padding: '24px', textAlign: 'center',
          border: '1px solid #e5e7eb', color: '#9ca3af', fontSize: 14,
        }}>
          No invoices yet. Invoices will appear here once billing is activated.
        </div>
      )}

      {/* S1 — affirmative-authorization screen (ROSCA / FPA v1.1 §4): discloses price, frequency,
          auto-renewal and cancellation, and requires a SEPARATE affirmative action before any charge. */}
      {authOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 480, width: '100%', padding: 28, boxShadow: '0 25px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: '0 0 6px' }}>Confirm your subscription</h2>
            <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 18px' }}>Review and authorize before we charge your card.</p>
            <div style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 12, padding: '16px 18px', marginBottom: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 14, color: '#374151' }}>Price</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>$89.00 / location</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 14, color: '#374151' }}>Your locations</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{checkoutQty}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, paddingTop: 10, borderTop: '1px dashed #e9d5ff' }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Total per month</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#7c3aed' }}>${89 * checkoutQty}.00 / mo</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 14, color: '#374151' }}>Billing frequency</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Monthly</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 14, color: '#374151' }}>Renewal</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Auto-renews monthly until cancelled</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <span style={{ fontSize: 14, color: '#374151', flexShrink: 0 }}>Cancellation</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', textAlign: 'right' }}>Cancel anytime from this dashboard; effective at the end of your current paid month</span>
              </div>
            </div>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginBottom: 20 }}>
              <input type="checkbox" checked={authChecked} onChange={function(e) { setAuthChecked(e.target.checked); }} style={{ marginTop: 3, width: 18, height: 18, accentColor: '#7c3aed', flexShrink: 0 }} />
              <span style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.5 }}>I authorize Groovie to charge my payment method <strong>${89 * checkoutQty}.00 per month</strong> ($89.00 &times; {checkoutQty} location{checkoutQty === 1 ? '' : 's'}), automatically renewing until I cancel. I can cancel anytime from my dashboard, effective at the end of the current paid month.</span>
            </label>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={function() { setAuthOpen(false); }} style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1.5px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Outfit',sans-serif" }}>Cancel</button>
              <button onClick={confirmAuthorizedSubscribe} disabled={!authChecked || !!upgrading} style={{ flex: 2, padding: '12px', borderRadius: 10, border: 'none', background: authChecked ? 'linear-gradient(135deg,#7c3aed,#a855f7)' : '#e5e7eb', color: authChecked ? '#fff' : '#9ca3af', fontSize: 14, fontWeight: 700, cursor: authChecked ? 'pointer' : 'not-allowed', fontFamily: "'Outfit',sans-serif" }}>{upgrading ? 'Redirecting…' : 'Authorize & Subscribe →'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
